import React, { useEffect, useState, useRef } from 'react';
import {
    Box,
    Container,
    Typography,
    Paper,
    Avatar,
    Chip,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Button,
    Card,
    CardContent,
    Grid,
    Breadcrumbs,
    Link,
    Snackbar

} from '@mui/material';
import { styled, useTheme, keyframes } from '@mui/material/styles';
import { useParams, Link as RouterLink } from 'react-router-dom';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ShareIcon from '@mui/icons-material/Share';

const gradient = keyframes`
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const BlogEntryContainer = styled(Container)(({ theme }) => ({
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(8),
    fontFamily: theme.typography.fontFamily
}));

const BlogHeader = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(8),
    textAlign: 'center',
    position: 'relative',
    padding: theme.spacing(10, 3),
    color: '#fff',
    background: theme.palette.primary.main,
    backgroundSize: '400% 400%',
    animation: `${gradient} 15s ease infinite`,
    borderRadius: theme.spacing(2),
    boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
    overflow: 'hidden',
    '&::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        background: 'rgba(0,0,0,0.05)',
        zIndex: 1
    }
}));

const AuthorSection = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    margin: theme.spacing(2, 0),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        textAlign: 'center',
        padding: theme.spacing(1.5),
        margin: theme.spacing(1, 0)
    }
}));

const MetadataChip = styled(Box)(({ theme }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.5, 1.5),
    borderRadius: theme.spacing(3),
    backgroundColor: 'rgba(52, 168, 83, 0.08)',
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: 1,
    border: `1px solid rgba(52, 168, 83, 0.12)`
}));

const ActionButtonGroup = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'center',
    [theme.breakpoints.down('sm')]: {
        width: '100%',
        '& button': {
            flex: 1
        }
    }
}));

const BlogContent = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(6),
    borderRadius: 20,
    boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
    fontFamily: theme.typography.fontFamily,
    '& h1, & h2, & h3, & h4, & h5, & h6': {
        fontWeight: 700,
        letterSpacing: '-0.025em',
        marginTop: theme.spacing(4),
        marginBottom: theme.spacing(2)
    },
    '& h1': {
        fontSize: '2.5rem',
        [theme.breakpoints.down('md')]: {
            fontSize: '2rem'
        }
    },
    '& h2': {
        fontSize: '2rem',
        color: '#34A853',
        [theme.breakpoints.down('md')]: {
            fontSize: '1.7rem'
        }
    },
    '& h3': {
        fontSize: '1.5rem',
        [theme.breakpoints.down('md')]: {
            fontSize: '1.3rem'
        }
    },
    '& p': {
        fontFamily: theme.typography.fontFamily,
        fontSize: '1.1rem',
        lineHeight: 1.8,
        marginBottom: theme.spacing(3),
        color: theme.palette.text.primary
    },
    '& .intro-paragraph': {
        fontSize: '1.25rem',
        fontWeight: 500,
        lineHeight: 1.7,
        color: theme.palette.text.primary,
        marginBottom: theme.spacing(5),
        padding: theme.spacing(3),
        backgroundColor: theme.palette.grey[50],
        borderRadius: 8,
        border: `1px solid ${theme.palette.grey[200]}`
    }
}));

const CategoryTag = styled(Box)(({ theme }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    padding: theme.spacing(0.5, 1.5),
    borderRadius: 16,
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    '&.sustainability': {
        backgroundColor: theme.palette.primary.light,
        color: '#FFFFFF',
        border: 'none'
    },
    '&.community': {
        backgroundColor: 'rgba(25, 118, 210, 0.1)',
        color: '#1976D2',
        border: '1px solid rgba(25, 118, 210, 0.2)'
    },
    '&.tips': {
        backgroundColor: 'rgba(237, 108, 2, 0.1)',
        color: '#ED6C02',
        border: '1px solid rgba(237, 108, 2, 0.2)'
    }
}));

const ActionButton = styled(Button)(({ theme }) => ({
    borderRadius: 30,
    padding: theme.spacing(1, 4),
    fontWeight: 600,
    textTransform: 'none',
    position: 'relative',
    overflow: 'hidden',
    transition: 'all 0.3s ease',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: '50%',
        left: '50%',
        width: 0,
        height: 0,
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.3)',
        transform: 'translate(-50%, -50%)',
        transition: 'width 0.6s, height 0.6s'
    },
    '&:hover::before': { width: 300, height: 300 },
    '&.primary': {
        backgroundColor: '#34A853',
        color: '#FFFFFF',
        '&:hover': {
            backgroundColor: '#2E7D32',
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 20px rgba(0,0,0,0.12)'
        }
    },
    '&.outlined': {
        borderColor: '#34A853',
        color: '#34A853',
        backgroundColor: 'transparent',
        '&:hover': {
            backgroundColor: 'rgba(52, 168, 83, 0.04)',
            borderColor: '#2E7D32',
            color: '#2E7D32',
            transform: 'translateY(-2px)'
        }
    }
}));

const StyledList = styled(List)(({ theme }) => ({
    marginLeft: theme.spacing(2),
    '& .MuiListItem-root': {
        paddingLeft: 0,
        alignItems: 'flex-start'
    },
    '& .MuiListItemIcon-root': {
        minWidth: 32,
        marginTop: 4
    },
    '& .MuiListItemText-root': {
        fontFamily: theme.typography.fontFamily
    }
}));

const ContentSection = styled(Box)(({ theme }) => ({
    opacity: 0,
    transform: 'translateY(40px)',
    transition: 'all 0.6s ease',
    '&.visible': {
        opacity: 1,
        transform: 'translateY(0)'
    }
}));

const RelatedCard = styled(Card)(({ theme }) => ({
    borderRadius: 16,
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 12px 24px rgba(0,0,0,0.12)'
    }
}));

export default function BlogEntry() {
    const { slug } = useParams();
    const theme = useTheme();
    const [blogData, setBlogData] = useState(null);
    const [visibleSections, setVisibleSections] = useState(new Set());
    const sectionRefs = useRef([]);
    const [snackbarOpen, setSnackbarOpen] = useState(false);

    // Mock blog data - in a real app, this would come from an API
    const blogDatabase = {
        'sustainable-living-guide-f7a2': {
            title: "The Ultimate Guide to Sustainable Living: How Borrowed Helps You Save Money and the Environment",
            author: "Marcella Arthur",
            date: "July 2, 2025",
            readTime: "8 min read",
            category: "sustainability",
            content: {
                intro:
                    "Sustainable living is more than a trend. It’s a response to the urgent need for environmental change. From rising global temperatures to resource scarcity, our daily choices have long-term consequences. The good news is that change doesn’t have to be radical. It starts with something simple: borrowing. Borrowing reduces waste and expense by shifting our focus from ownership to access. Whether it’s a blender, a ball gown, or a leaf blower, Borrowed.com makes it easy to borrow what you need and return it when you’re done. No clutter. No waste. No unnecessary spending.",
                sections: [
                    {
                        title: "What Is Sustainable Living?",
                        content:
                            "At its core, sustainable living means choosing actions that support the planet’s ability to renew itself. That includes conserving water, minimizing waste, and reducing demand for energy and materials. Borrowing fits seamlessly into this lifestyle. It lowers the demand for production, which reduces emissions and raw material extraction. According to the EPA, more than 12 million tons of furniture and furnishings were thrown away in 2018. Borrowing helps keep usable goods in circulation and out of landfills."
                    },
                    {
                        title: "Why Borrowing Saves You Money",
                        content: "Borrowing helps you sidestep large purchases for things you’ll use once or twice.",
                        list: [
                            "Power tools: Renting a circular saw beats spending a hundred dollars on one you’ll use once a year.",
                            "Formalwear: Borrow a tux or gown instead of paying retail for something that will collect dust.",
                            "Seasonal gear: Skip the snow blower purchase if you only get snow a few days a year.",
                            "The bonus? You’re not paying for storage, repairs, or replacements. You’re also making better use of your living space."
                        ]
                    },
                    {
                        title: "Environmental Impact of Borrowing",
                        content:
                            "Borrowing reduces the number of new products entering the market, which means less manufacturing, less packaging, and fewer carbon emissions. A 2021 study published in Nature Sustainability found that extending the lifespan of household products by even one-third could cut emissions by 20 to 30 percent. Borrowing also supports the circular economy. It reduces the pressure to extract raw materials like copper, lithium, and water-intensive cotton—resources that are becoming harder to obtain without harming ecosystems."
                    },
                    {
                        title: "Social and Lifestyle Benefits",
                        content:
                            "Borrowing doesn’t just help the planet and your wallet. It builds community. On Borrowed.com, you connect with others who care about thoughtful consumption. You get what you need and give others the opportunity to do the same. You’ll also find yourself shopping less impulsively and living more intentionally. That’s better for your space, your stress levels, and your long-term financial goals."
                    },
                    {
                        title: "How Borrowed Makes It Simple",
                        list: [
                            "A growing catalog of high-quality items",
                            "Transparent pricing and no surprise fees",
                            "Real-time environmental impact data so you can see the difference you’re making"
                        ]
                    },
                    {
                        title: "Call to Action",
                        content:
                            "Borrowing is an easy way to live sustainably without sacrificing convenience or comfort. Whether you're just starting or fully committed to a more intentional lifestyle, make Borrowed.com your go-to place to get what you need, when you need it—without buying it. Explore more. Waste less. Get started at Borrowed.com."
                    }
                ]
            }
        }
    };


    const handleShare = () => {
        // if (navigator.share) {
        //     navigator.share({ title: blogData.title, url: window.location.href });
        // } else if (navigator.clipboard) {
        //     navigator.clipboard.writeText(window.location.href).then(() => {
        //         alert('Link copied to clipboard');
        //     });
        // }
        navigator.clipboard.writeText(window.location.href).then(() => {
            setSnackbarOpen(true);
        });
    };

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbarOpen(false);
    };

    useEffect(() => {
        const data = blogDatabase[slug];
        if (data) {
            setBlogData(data);
        }
    }, [slug]);

    useEffect(() => {
        if (!blogData) return;
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const idx = parseInt(entry.target.dataset.index, 10);
                    setVisibleSections((prev) => new Set([...prev, idx]));
                    observer.unobserve(entry.target);
                }
            });
        }, { threshold: 0.15, rootMargin: '0px 0px -40px 0px' });

        sectionRefs.current.forEach((ref) => ref && observer.observe(ref));

        return () => observer.disconnect();
    }, [blogData]);

    /*
     * Previous related posts kept for reference
     * [
     *   { title: "10 Essential Items You Should Never Buy Again", slug: "never-buy-again-items-b3f1" },
     *   { title: "Building Trust in Your Community", slug: "building-community-trust-c8e5" },
     *   { title: "The Environmental Impact of Sharing", slug: "environmental-impact-sharing-d9a7" }
     * ]
     */

    const relatedPosts = [
        {
            title: 'Why Buy When You Can Borrow? Ten Everyday Items You Should Stop Buying',
            slug: 'why-buy-when-you-can-borrow',
            category: 'tips'
        },
        {
            title: 'Why Minimalism and Borrowing Go Hand in Hand',
            slug: 'minimalism-and-borrowing',
            category: 'sustainability'
        },
        {
            title: 'The Environmental Cost of Fast Fashion—and How Borrowing Can Help',
            slug: 'fast-fashion-borrowing',
            category: 'community'
        },
        {
            title: 'Borrowing for Businesses: Competing with Amazon and Walmart by Offering Rentals in Your Store',
            slug: 'borrowing-for-businesses',
            category: 'tips'
        }
    ];

    if (!blogData) {
        return (
            <BlogEntryContainer
                maxWidth="lg" >
                <Typography variant="h4" align="center">
                    Blog post not found
                </Typography>
                <Box sx={{ textAlign: 'center', mt: 4 }}>
                    <ActionButton
                        component={RouterLink}
                        to="/blog"
                        className="primary"
                        startIcon={<ArrowBackIcon />}
                    >
                        Back to Blog
                    </ActionButton>
                </Box>
            </BlogEntryContainer>
        );
    }

    return (
        <BlogEntryContainer maxWidth="lg">
            {/* Breadcrumbs */}
            <Breadcrumbs
                separator={<NavigateNextIcon fontSize="small" />}
                sx={{ mb: 4, fontFamily: theme.typography.fontFamily }}
            >
                <Link component={RouterLink} to="/" color="inherit">
                    Home
                </Link>
                <Link component={RouterLink} to="/blog" color="inherit">
                    Blog
                </Link>
                <Typography color="text.primary" sx={{ fontFamily: theme.typography.fontFamily }}>
                    {blogData.title}
                </Typography>
            </Breadcrumbs>

            {/* Back Button */}
            <Box sx={{ mb: 4 }}>
                <ActionButton
                    component={RouterLink}
                    to="/blog"
                    className="outlined"
                    startIcon={<ArrowBackIcon />}
                >
                    Back to Blog
                </ActionButton>
            </Box>

            <BlogContent>
                {/* Article Header */}
                <BlogHeader>
                    <Box sx={{ mb: 4 }}>
                        <CategoryTag className={blogData.category}>
                            {blogData.category === 'sustainability' ? 'Sustainability' :
                                blogData.category === 'community' ? 'Community' :
                                    blogData.category === 'tips' ? 'Tips & Guides' : blogData.category}
                        </CategoryTag>
                    </Box>

                    <Typography
                        variant="h1"
                        component="h1"
                        gutterBottom
                        sx={{
                            fontWeight: 700,
                            letterSpacing: '-0.03em',
                            fontSize: { xs: '2.2rem', md: '3.2rem' },
                            lineHeight: { xs: 1.2, md: 1.1 },
                            maxWidth: '900px',
                            margin: '0 auto',
                            mb: 2
                        }}
                    >
                        {blogData.title}
                    </Typography>

                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 400,
                            fontSize: '1.1rem',
                            maxWidth: '700px',
                            margin: '0 auto',
                            mb: 4,
                            lineHeight: 1.6
                        }}
                    >
                        Discover practical strategies for sustainable living that benefit both your wallet and the planet.
                    </Typography>

                    <AuthorSection>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar
                                sx={{
                                    bgcolor: '#34A853',
                                    width: 56,
                                    height: 56,
                                    border: '3px solid white',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                }}
                            >
                                <PersonIcon sx={{ fontSize: 28 }} />
                            </Avatar>
                            <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: 600,
                                        mb: 0.5,
                                        color: theme.palette.text.primary
                                    }}
                                >
                                    {blogData.author}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ fontWeight: 500 }}
                                >
                                    Chief Business Officer
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
                            <MetadataChip>
                                <CalendarTodayIcon sx={{ fontSize: 16 }} />
                                <Typography
                                    variant="body2"
                                    component="span"
                                    sx={{ fontWeight: 500, display: 'inline-flex', alignItems: 'center', lineHeight: 1 }}
                                >
                                    {blogData.date}
                                </Typography>
                            </MetadataChip>

                            <MetadataChip>
                                <AccessTimeIcon sx={{ fontSize: 16 }} />
                                <Typography
                                    variant="body2"
                                    component="span"
                                    sx={{ fontWeight: 500, display: 'inline-flex', alignItems: 'center', lineHeight: 1 }}
                                >
                                    {blogData.readTime}
                                </Typography>
                            </MetadataChip>
                        </Box>

                        <ActionButtonGroup>
                            {/* <ActionButton
                                className="outlined"
                                size="medium"
                                startIcon={<ShareIcon />}
                                onClick={handleShare}
                                sx={{ minWidth: 100 }}
                            >
                                Share
                            </ActionButton> */}
                        </ActionButtonGroup>
                    </AuthorSection>
                </BlogHeader>

                <Divider sx={{ mb: 6 }} />

                {/* Article Content */}
                <Box sx={{
                    width: '100%',
                    maxWidth: { xs: '100%', md: '800px' },
                    mx: 'auto',
                    px: { xs: 0, md: 0 }
                }}>
                    {/* Introduction */}
                    <ContentSection
                        ref={(el) => (sectionRefs.current[0] = el)}
                        data-index={0}
                        className={visibleSections.has(0) ? 'visible' : ''}
                        sx={{ mb: 5 }}
                    >
                        <Typography className="intro-paragraph">
                            {blogData.content.intro}
                        </Typography>
                    </ContentSection>

                    {/* Main Content Sections */}
                    {blogData.content.sections.map((section, index) => (
                        <ContentSection
                            key={index + 1}
                            ref={(el) => (sectionRefs.current[index + 1] = el)}
                            data-index={index + 1}
                            className={visibleSections.has(index + 1) ? 'visible' : ''}
                            sx={{ mb: 5 }}
                        >
                            <Typography variant="h2" component="h2">
                                {section.title}
                            </Typography>
                            <Typography paragraph>
                                {section.content}
                            </Typography>

                            {section.list && (
                                <StyledList>
                                    {section.list.map((item, listIndex) => (
                                        <ListItem key={listIndex}>
                                            <ListItemIcon>
                                                <CheckCircleIcon color="primary" fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={item}
                                                primaryTypographyProps={{
                                                    fontFamily: theme.typography.fontFamily,
                                                    fontSize: '1.1rem'
                                                }}
                                            />
                                        </ListItem>
                                    ))}
                                </StyledList>
                            )}
                        </ContentSection>
                    ))}

                    {/* Call to Action */}
                    <Box sx={{
                        textAlign: 'center',
                        mt: 8,
                        p: 4,
                        backgroundColor: theme.palette.grey[50],
                        borderRadius: 3
                    }}>
                        <Typography
                            variant="h4"
                            gutterBottom
                            sx={{
                                fontWeight: 700,
                                color: '#34A853'
                            }}
                        >
                            Ready to Start Your Sustainable Journey?
                        </Typography>
                        <Typography
                            variant="body1"
                            paragraph
                            sx={{
                                fontSize: '1.1rem',
                                mb: 4
                            }}
                        >
                            Join thousands of community members who are saving money and helping the environment through sharing.
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                            <ActionButton
                                className="primary"
                                size="large"
                                component={RouterLink}
                                to="/listings"
                            >
                                Browse Items
                            </ActionButton>
                            <ActionButton
                                className="outlined"
                                size="large"
                                component={RouterLink}
                                to="/create-listing"
                            >
                                List an Item
                            </ActionButton>
                        </Box>
                    </Box>
                </Box>
            </BlogContent>

            {/* Related Articles */}
            <Box sx={{ mt: 8 }}>
                <Typography
                    variant="h4"
                    gutterBottom
                    sx={{
                        fontWeight: 700,
                        textAlign: 'center',
                        mb: 4
                    }}
                >
                    Related Articles
                </Typography>
                <Grid container spacing={3}>
                    {relatedPosts.map((post, index) => (
                        <Grid item xs={12} md={4} key={index}>
                            <RelatedCard
                            // onClick={() => window.location.href = `/blog/${post.slug}`}
                            >
                                <CardContent sx={{ p: 3 }}>
                                    <Box sx={{ mb: 2 }}>
                                        <CategoryTag className={post.category}>
                                            {post.category === 'sustainability' ? 'Sustainability' :
                                                post.category === 'community' ? 'Community' :
                                                    post.category === 'tips' ? 'Tips & Guides' : post.category}
                                        </CategoryTag>
                                    </Box>
                                    <Typography
                                        variant="h6"
                                        fontWeight={600}
                                        sx={{
                                            lineHeight: 1.4
                                        }}
                                    >
                                        {post.title}
                                    </Typography>
                                </CardContent>
                            </RelatedCard>
                        </Grid>
                    ))}
                </Grid>
            </Box>
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={2000}
                onClose={handleSnackbarClose}
                message="Copied to clipboard"
            />
        </BlogEntryContainer>
    );
}
